import { AwsRum, AwsRumConfig, Telemetry } from 'aws-rum-web';
import { RumMidwayCredentialsProvider } from '@amzn/rum-midway-credentials-provider';
import { getRumConfig } from 'src/components/common/util/RumConfigHelpers';
import { RumClickEvent } from 'src/common/constants';

// Only AppSync gets X-Amzn-Trace-Id; the Midway /sso preflight does not allow the header.
const RUM_XRAY_TRACED_URLS: RegExp[] = [
  /^https:\/\/[^/]+\.appsync-api\.[^.]+\.amazonaws\.com\//,
];

/**
 * RUM telemetry collectors to enable (see AwsRumConfig.telemetries):
 *  - errors: UI-side JavaScript errors.
 *  - performance: page-load and web-vitals metrics.
 *  - http: outgoing request timing/status (Default: enabled for failed requests only).
 */
const RUM_TELEMETRIES: Telemetry[] = [
  'errors',
  'performance',
  ['http', { addXRayTraceIdHeader: RUM_XRAY_TRACED_URLS }],
];

// App version reported to RUM for the AwsRum client.
const RUM_APP_VERSION = '1.0.0';

// Session length. Default is 1800s (30 min); extended to 10 hours to match the Cognito timing.
const RUM_SESSION_LENGTH_SECONDS = 10 * 60 * 60;

// RUM data-plane endpoint. Region-scoped, so it's built from the resolved config region rather than
// hardcoded as a magic string at the call site.
const rumDataPlaneEndpoint = (region: string): string =>
  `https://dataplane.rum.${region}.amazonaws.com`;

/**
 * CloudWatch RUM (Real User Monitoring) bootstrap for the LangHub UI.
 *
 * Uses **Midway** authorization, the `@amzn/rum-midway-credentials-provider` fetches a Midway id
 * token and exchanges it via STS `AssumeRoleWithWebIdentity` for temporary `rum:PutRumEvents`
 * credentials scoped to a monitoring role.
 *
 * - {@link initRum} constructs the RUM client once. It is a **safe no-op** when RUM is unconfigured
 *   or if construction throws — RUM must never break the app.
 * - {@link recordRumPageView} emits a page view for a normalized, low-cardinality page id.
 * - {@link recordRumEvent} emits a custom event for a user action (e.g. a search). No-op until init.
 */

// Capture the ORIGINAL window.fetch at module load, BEFORE any override replaces
// it. RumMidwayCredentialsProvider must use the genuine fetch to reach Midway/STS — SentryFetch
// appends `/sso/login` to requests and breaks them. Passing the original fetch avoids that.
// (In non-browser/test contexts window may be undefined; fall back to undefined -> provider default.)
const ORIGINAL_FETCH: typeof fetch | undefined =
  typeof window !== 'undefined' && window.fetch
    ? window.fetch.bind(window)
    : undefined;

let awsRum: AwsRum | undefined;

/**
 * Initialize the RUM client once, using the Midway credential provider. Safe to call
 * unconditionally; no-ops when RUM is unconfigured for this build/stage.
 */
export function initRum(): void {
  if (awsRum) return;

  const config = getRumConfig();
  if (!config) return; // RUM not configured for this build/stage

  try {
    const rumConfig: AwsRumConfig = {
      sessionSampleRate: 1,
      endpoint: rumDataPlaneEndpoint(config.region),
      telemetries: [...RUM_TELEMETRIES],
      allowCookies: true,
      // Required for the http plugin to open a trace and send the trace header at all.
      enableXRay: true,
      sessionLengthSeconds: RUM_SESSION_LENGTH_SECONDS,
      // LangHub is an SPA with id-bearing routes (/ns/<id>/...). The SDK's automatic page-view
      // recording would use the raw pathname, making every id its own page (cardinality blows up).
      // Disable it and record NORMALIZED page ids ourselves via recordRumPageView (see
      // usePageViewMetrics). NOTE: with this true, RUM records NO page views unless we emit them —
      // every page we want counted (Search, Track, Landing, Namespace, ...) must call
      // recordRumPageView.
      disableAutoPageView: true,
    };

    awsRum = new AwsRum(
      config.applicationId,
      RUM_APP_VERSION,
      config.region,
      rumConfig,
    );

    // Install the Midway credential provider: fetch a Midway id token and exchange it via STS for
    // temporary rum:PutRumEvents credentials. ChainAnonymousCredentialsProvider caches + renews.
    const credentialProvider = new RumMidwayCredentialsProvider(
      config.region,
      config.relyingParty,
      config.monitoringRoleArn,
      ORIGINAL_FETCH,
    ).ChainAnonymousCredentialsProvider;
    awsRum.setAwsCredentials(credentialProvider);
  } catch (error) {
    // Never let RUM initialization break the app; log a non-sensitive marker only.
    console.error('CloudWatch RUM init failed; continuing without RUM', error);
    awsRum = undefined;
  }
}

/**
 * Record a page view for a normalized, low-cardinality page id. No-op when RUM is not initialized.
 *
 * Because auto page views are disabled (see {@link initRum}), this is the ONLY source of RUM page
 * views — call it on every route change with a stable page id (never a raw namespace/string id).
 *
 * @param pageId a stable, low-cardinality page id (e.g. 'Search', 'Namespace', 'Landing', 'Help').
 */
export function recordRumPageView(pageId: string): void {
  if (!awsRum) return;
  try {
    awsRum.recordPageView(pageId);
  } catch (error) {
    console.error('CloudWatch RUM recordPageView failed', error);
  }
}

/**
 * Record a custom RUM event for a user action. No-op when RUM is not initialized.
 *
 * @param eventType a {@link RUM_CLICK_EVENT} name (centralized; ad-hoc strings are rejected by the type).
 * @param eventData non-sensitive attributes only — never source/translation content or auth tokens;
 *        keep to safe identifiers (namespace, ids, type).
 */
export function recordRumEvent(
  eventType: RumClickEvent,
  eventData: Record<string, unknown> = {},
): void {
  if (!awsRum) return;
  try {
    awsRum.recordEvent(eventType, eventData);
  } catch (error) {
    console.error('CloudWatch RUM recordEvent failed', error);
  }
}
